import { useEffect, useRef } from 'react';
import type { Room } from 'livekit-client';
import type { LayerTree } from '@liveforge/shared';
import { CompositorRenderer, FULL_QUALITY } from './renderer';
import { TrackRegistry } from './track-registry';

export interface CompositorCanvasProps {
  room: Room | undefined;
  layers: LayerTree;
  /**
   * CSS size of the element. Independent of what the canvas is composed at:
   * the layer tree is authored in 1920x1080 space and scaled to fit.
   */
  className?: string;
  /**
   * Pixel size the frame is actually composed at, and an upper bound on how
   * often. Defaults to the full broadcast size at display refresh rate.
   *
   * Lower them wherever the picture is not the output: a preview a few hundred
   * pixels wide costs exactly as much as Egress's frame otherwise, which on a
   * phone is enough to make the browser throttle the call itself. Passed as
   * three numbers rather than an object so a caller cannot re-mount the loop
   * by forgetting to memoise it.
   */
  renderWidth?: number;
  renderHeight?: number;
  maxFps?: number;
  /**
   * Draws a labelled marker where a layer has nothing to show. On for the
   * director's preview, off everywhere the picture is an output - a slot the
   * host has not filled yet is studio business, not the audience's.
   */
  showPlaceholders?: boolean;
  /**
   * Hands out a reader for the renderer's frame counters, and null on unmount.
   *
   * Only the Layout Page's debug overlay wants this, and it wants it because
   * counting animation frames from outside measures the loop rather than the
   * work - with a frame cap those are different numbers, and the difference is
   * exactly what a juddering recording is made of.
   */
  onCounters?: (read: (() => { frames: number; ms: number }) | null) => void;
}

/**
 * The composited output. Mounted full-size by the Layout Page for Egress, and
 * scaled down inside the studio as the host's preview - the same component in
 * both, which is what makes the preview trustworthy.
 */
export function CompositorCanvas({
  room,
  layers,
  className,
  renderWidth = FULL_QUALITY.width,
  renderHeight = FULL_QUALITY.height,
  maxFps = FULL_QUALITY.maxFps,
  showPlaceholders = false,
  onCounters,
}: CompositorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const registryRef = useRef<TrackRegistry | null>(null);
  const rendererRef = useRef<CompositorRenderer | null>(null);

  // The renderer and registry outlive individual renders and own real
  // resources - a frame loop and a video element per track - so they are built
  // once and torn down on unmount, never rebuilt when `layers` changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const registry = new TrackRegistry();
    const renderer = new CompositorRenderer(registry);
    registryRef.current = registry;
    rendererRef.current = renderer;
    // Started at the default size; the quality effect below runs immediately
    // after this one and applies the real one before anything is drawn. That
    // keeps the props out of a mount-only effect's dependencies.
    renderer.start(canvas);

    return () => {
      renderer.stop();
      registry.detach();
      registryRef.current = null;
      rendererRef.current = null;
    };
  }, []);

  // Separate from the mount effect so a caller that passes an unmemoised
  // callback cannot tear down the draw loop and the track registry with it.
  useEffect(() => {
    if (!onCounters) return;
    const renderer = rendererRef.current;
    onCounters(renderer ? () => renderer.counters() : null);
    return () => onCounters(null);
  }, [onCounters]);

  useEffect(() => {
    const registry = registryRef.current;
    if (!registry || !room) return;
    registry.attach(room);
    return () => registry.detach();
  }, [room]);

  // Pushed into the renderer rather than held in React state: the draw loop
  // reads it every frame, and re-rendering the component for each edit would
  // buy nothing.
  useEffect(() => {
    rendererRef.current?.setLayers(layers);
  }, [layers]);

  // Resizing a running renderer rather than restarting it: a re-mount would
  // rebuild a video element per participant, and every one of them would have
  // to re-attach its track.
  useEffect(() => {
    rendererRef.current?.setQuality({ width: renderWidth, height: renderHeight, maxFps });
  }, [renderWidth, renderHeight, maxFps]);

  useEffect(() => {
    rendererRef.current?.setPlaceholders(showPlaceholders);
  }, [showPlaceholders]);

  return <canvas className={className} ref={canvasRef} />;
}
